import React, { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useForm, FormProvider, useWatch, useFormState } from 'react-hook-form';
import { Grid } from '@material-ui/core';
import RMSCommonDialog from '../../../shared/components/dialog/RMSCommonDialog';
import { RMSSelect, RMSDateTimePicker } from '../../../shared/components/form-controls';
import { RMSConfirmationDialog } from '../../../shared/components/dialog';
import RMSRecordHistoryButton from '../../../shared/components/modules/RMSRecordHistoryButton';
import SearchPersonAutoComplete from '../../../shared/components/modules/SearchPersonAutoComplete';
import AddPersonDialog from './AddPersonDialog';
import CreateFormResolver from '../../../shared/utility/createFormResolver';
import { executeApiAction } from '../../../shared/utility/apiHelper';
import { notify } from 'reducers/NotifierReducer';
import useConfirmDialog from '../../../shared/utility/useConfirmDialog';
import useUserDictionaryOptions from '../../hooks/useUserDictionaryOptions';
import { savePrePlanInspection } from '../../services/userService';
import { DICT_SELECT_PROPS } from '../../constants/addressPrePlanInspectionFormConstants';
import inspectionSchema from '../../schema/addressPrePlanInspectionSchema';

const AddressPrePlanInspectionDialog = ({
  open,
  onClose,
  ptsAddressPrePlanID,
  inspection = null,
  onSaved,
}) => {
  const dispatch = useDispatch();
  const [submitting, setSubmitting] = useState(false);
  const [addPersonDialogOpen, setAddPersonDialogOpen] = useState(false);
  const isEditMode = Boolean(inspection?.id);
  const { designationOptions } = useUserDictionaryOptions();

  const methods = useForm({
    resolver: CreateFormResolver(inspectionSchema),
    mode: 'onSubmit',
    defaultValues: {
      InspectionDateTime: inspection?.InspectionDate || new Date(),
      ptsPersonID: inspection?.ptsPersonID ?? null,
      ShiftDesignation: inspection?.ShiftDesignation || '',
    },
  });

  const { isDirty } = useFormState({ control: methods.control });
  const { confirmState, requestConfirm, handleYes, handleNo } = useConfirmDialog();
  const ptsPersonID = useWatch({ control: methods.control, name: 'ptsPersonID' });

  const handlePersonCreated = useCallback(
    (person) => {
      methods.setValue('ptsPersonID', person?.ptsPersonID ?? null, {
        shouldDirty: true,
        shouldValidate: true,
      });
      setAddPersonDialogOpen(false);
    },
    [methods]
  );

  const onSubmit = useCallback(
    async (data) => {
      const payload = {
        ptsAddressPrePlanInspectionID: isEditMode ? inspection.id : null,
        ptsAdddressPrePlanID: ptsAddressPrePlanID,
        InspectionDateTime: data.InspectionDateTime || null,
        ptsPersonID: data.ptsPersonID,
        ShiftDesignation: data.ShiftDesignation,
        IsDeleted: false,
      };

      await executeApiAction({
        setLoading: setSubmitting,
        dispatch,
        action: () => savePrePlanInspection(payload),
        onSuccess: () => {
          dispatch(
            notify(
              isEditMode ? 'Inspection updated successfully' : 'Inspection added successfully',
              'success'
            )
          );
          onSaved?.();
        },
      });
    },
    [isEditMode, inspection, ptsAddressPrePlanID, dispatch, onSaved]
  );

  const handleSave = methods.handleSubmit(onSubmit);

  const requestClose = useCallback(() => {
    if (isDirty) {
      requestConfirm(
        'You have unsaved changes on this inspection. Are you sure you want to close without saving?',
        () => onClose?.(),
        'Unsaved Changes'
      );
      return;
    }

    onClose?.();
  }, [isDirty, requestConfirm, onClose]);

  return (
    <>
      <RMSCommonDialog
        open={open}
        title={isEditMode ? 'Edit Inspection' : 'Add Inspection'}
        onClose={requestClose}
        onSave={handleSave}
        loading={submitting}
        saveDisabled={submitting}
        maxWidth="sm"
        fullWidth
        closeOnBackdropClick={false}
        saveButton={{
          show: true,
          type: 'simple',
          label: 'Save',
          variant: 'contained',
          color: 'primary',
        }}
        cancelButton={{
          show: true,
          type: 'simple',
          label: 'Close',
          variant: 'outlined',
          color: 'secondary',
        }}
        leftActions={
          isEditMode && (
            <RMSRecordHistoryButton
              disabled={!inspection?.Created}
              created={inspection?.Created}
              createdBy={inspection?.CreatedBy}
              updated={inspection?.Updated}
              updatedBy={inspection?.UpdatedBy}
            />
          )
        }>
        <FormProvider {...methods}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <RMSDateTimePicker name="InspectionDateTime" label="Inspection Date Time" />
            </Grid>
            <Grid item xs={12}>
              <SearchPersonAutoComplete
                name="ptsPersonID"
                label="Person"
                defaultPersonId={ptsPersonID}
                onAddIconClick={() => setAddPersonDialogOpen(true)}
                showDetailsIcon={false}
              />
            </Grid>
            <Grid item xs={12}>
              <RMSSelect
                name="ShiftDesignation"
                label="Shift Designation"
                options={designationOptions}
                {...DICT_SELECT_PROPS}
              />
            </Grid>
          </Grid>
        </FormProvider>

        {addPersonDialogOpen && (
          <AddPersonDialog
            open={addPersonDialogOpen}
            onClose={() => setAddPersonDialogOpen(false)}
            onPersonCreated={handlePersonCreated}
          />
        )}
      </RMSCommonDialog>

      {confirmState.open && (
        <RMSConfirmationDialog
          open={confirmState.open}
          title={confirmState.title}
          message={confirmState.message}
          onNo={handleNo}
          onYes={handleYes}
        />
      )}
    </>
  );
};

export default AddressPrePlanInspectionDialog;
